import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Partner {
  id: string;
  name: string;
  logo_url: string;
  country: string;
  category: string;
  description: string;
  website_url: string;
  is_active: boolean;
  created_at: string;
}

const PartnersPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setPartners(data || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats dynamically
  const totalPartners = partners.length;
  const uniqueCountries = new Set(partners.map(p => p.country)).size;

  const stats = [
    {
      icon: 'ri-global-line',
      value: `${totalPartners}+`,
      label: 'Partenaires internationaux',
    },
    {
      icon: 'ri-map-pin-line',
      value: `${uniqueCountries}+`,
      label: 'Pays représentés',
    },
    {
      icon: 'ri-time-line',
      value: '8+',
      label: 'Années d\'expérience',
    },
    {
      icon: 'ri-shield-check-line',
      value: '100%',
      label: 'Produits certifiés',
    },
  ];

  // Get unique categories with counts
  const categoryMap = new Map<string, number>();
  partners.forEach(partner => {
    const count = categoryMap.get(partner.category) || 0;
    categoryMap.set(partner.category, count + 1);
  });

  const filters = [
    { id: 'all', label: 'Tous les partenaires', count: totalPartners },
    ...Array.from(categoryMap.entries()).map(([category, count]) => ({
      id: category,
      label: category,
      count,
      icon: getCategoryIcon(category)
    }))
  ];

  // Filter partners based on active filter
  const filteredPartners = activeFilter === 'all' 
    ? partners 
    : partners.filter(p => p.category === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section with Image */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4884bbcad76b47444638606fe722184b.jpg"
            alt="Partenaires ADF Biomédical"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Nos Partenaires
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Des collaborations internationales pour vous offrir les meilleurs dispositifs médicaux
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 text-center"
              >
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-[#E31B23]`}></i>
                </div>
                <div className="text-3xl font-bold text-[#1A1A1A] mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6">
              Nos Partenaires
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Nous collaborons avec les leaders mondiaux de l'industrie médicale pour vous offrir les meilleures solutions et équipements de santé.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters Section */}
      {!loading && partners.length > 0 && (
        <section className="py-8 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeFilter === filter.id
                      ? 'bg-[#E31B23] text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {filter.icon && (
                    <i className={`${filter.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                  )}
                  <span>{filter.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    activeFilter === filter.id
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {filter.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Loading State */}
      {loading && (
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i className="ri-loader-4-line animate-spin text-5xl text-[#E31B23]"></i>
              </div>
              <p className="text-gray-600">Chargement des partenaires...</p>
            </div>
          </div>
        </section>
      )}

      {/* Partners Grid */}
      {!loading && filteredPartners.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPartners.map((partner, index) => (
                <motion.div
                  key={partner.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {/* Logo */}
                  <div className="relative w-full h-48 bg-gray-50 flex items-center justify-center p-6">
                    <img
                      src={partner.logo_url}
                      alt={partner.name}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/300x200?text=Logo';
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-3">
                      {partner.name}
                    </h3>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <i className="ri-map-pin-line text-[#E31B23]"></i>
                      <span>{partner.country}</span>
                    </div>

                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-[#E31B23]/10 text-[#E31B23] text-xs font-medium rounded-full">
                        {partner.category}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 line-clamp-3 mb-6 leading-relaxed">
                      {partner.description}
                    </p>

                    {/* Website Link */}
                    <a
                      href={partner.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#E31B23] text-white rounded-lg text-sm font-medium hover:bg-[#C41820] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <span>Visiter le site</span>
                      <i className="ri-external-link-line text-base w-4 h-4 flex items-center justify-center"></i>
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!loading && partners.length === 0 && (
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center py-16"
            >
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-6xl text-gray-300"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Aucun partenaire disponible
              </h3>
              <p className="text-gray-500">
                Nous travaillons actuellement à élargir notre réseau de partenaires
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* No Results State */}
      {!loading && partners.length > 0 && filteredPartners.length === 0 && (
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center py-16"
            >
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-folder-open-line text-6xl text-gray-300"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Aucun partenaire trouvé dans cette catégorie
              </h3>
              <p className="text-gray-500">
                Essayez de sélectionner une autre catégorie
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg p-12 md:p-16 text-center border border-gray-100"
          >
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">
                Devenez Notre Partenaire
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Vous êtes un fabricant ou distributeur d'équipements médicaux ? Rejoignez notre réseau de partenaires de confiance
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#E31B23] text-white rounded-lg font-semibold text-base hover:bg-[#C41820] transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap"
              >
                <span>Contactez-nous</span>
                <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Helper function to get category icon
function getCategoryIcon(category: string): string {
  const iconMap: Record<string, string> = {
    'Équipements médicaux': 'ri-hospital-line',
    'Imagerie médicale': 'ri-scan-line',
    'Diagnostic in vitro': 'ri-test-tube-line',
    'Dispositifs médicaux': 'ri-stethoscope-line',
    'Imagerie et monitoring': 'ri-heart-pulse-line',
    'Solutions de santé': 'ri-health-book-line',
  };
  return iconMap[category] || 'ri-building-line';
}

export default PartnersPage;
